// =============================================================================
// domain/journal.ts — the journal entry, and the load-time corrections it needs.
// -----------------------------------------------------------------------------
// PORTED FROM the old client's `migrateJournalEntry` plus the three journal
// blocks in `migrateState` — semantics, not code (D3).
//
// ── 🚨 THE ONE THAT MATTERS: `eod_mistakes` AND `eod_wins` ARE SWAPPED IN THE DB
//
// The old client's HTML labels said "Things I Did Well" / "Things I Need to Fix"
// while the underlying input ids were the other way round. So for years,
// "did well" content was written into `eod_mistakes` and "need to fix" into
// `eod_wins`. It was self-consistent INSIDE the app — every read went through the
// same inverted names, so the user saw their text under the right heading — but
// the database columns hold semantically wrong content.
//
// The old client fixes it on load and marks the entry:
//
//     if (j.booleans._eodRecapSwapMigrated) continue;      // already corrected
//     swap(j.eodRecap.mistakes, j.eodRecap.wins);
//     j.booleans._eodRecapSwapMigrated = true;
//
// and writes the corrected values back only "on next user-triggered save per
// entry". **So the live table holds a MIX**: entries that have been re-saved
// since are correct on disk, entries that have not are still inverted, and the
// only way to tell them apart is that marker inside `booleans`.
//
// ⚠ A port that reads these two columns raw shows, for every un-migrated entry,
// the trader's wins under "what broke" and their mistakes under "what worked".
// Not a rounding error — the exact inverse of what they wrote, on the surface
// whose whole purpose is honest self-review. And it looks completely normal.
//
// ── THE OTHERS ──
//
// preMarketPlan shape: legacy `news` string → `newsEvents[]`, legacy `bias` →
// `marketRegimeAnalysis`, legacy `keyLevels` → `gammaRegimeAnalysis`.
//
// Weekend re-keying and empty-weekend pruning are NOT here: both MUTATE which
// date an entry belongs to and both are write-shaped. They are recorded in the
// io module's header as unported, because doing them at read time would make the
// same entry appear under two dates depending on which client opened it.
// =============================================================================

/** One news event on the pre-market plan. */
export interface NewsEvent {
  time: string;
  event: string;
  plan: string;
}

/**
 * The levels block. ⚠ ALL 46 live entries carry these, and the first port
 * dropped every one of them — a side-by-side question ("is this supposed to look
 * like Beta?") is what surfaced it. They are the whole content of a pre-market
 * plan for a levels-driven trader.
 *
 * Numbers or null; null means "not marked up", which is different from zero.
 */
export interface KeyLevels {
  pdVah: number | null;
  pdVal: number | null;
  pdPoc: number | null;
  prevHigh: number | null;
  prevLow: number | null;
  overnightHigh: number | null;
  overnightLow: number | null;
  callWall1: number | null;
  callWall2: number | null;
  putWall1: number | null;
  putWall2: number | null;
  hvl: number | null;
  mvc: number | null;
  lis: number | null;
  /** Free text explaining the line-in-sand. */
  lisSummary: string;
}

/** Pre-market state, self-reported. Drives the correlation analytics later. */
/**
 * ⚠ THE FOUR RATINGS ARE NUMBERS, and typing them as `string` erased real data.
 *
 * The old client's sliders write `sa[key] = +slider.value` — a NUMBER 1–5. Modelled
 * as `string` and parsed with `str()`, every one of them became `''`, so the panel
 * read "Not recorded" over a completed self-assessment. Confirmed live on Josh's
 * 2026-07-30 entry, which stores `{energy: 4, focus: 4, mood: 4}`.
 *
 * `number | null` matches storage and makes a write non-lossy. A legacy row that
 * stored a text label (`'High'`) keeps it — see `ratingOrNull`.
 */
export type Rating = number | string | null;

export interface SelfAssessment {
  sleepHours: number | null;
  caffeineCups: number | null;
  energy: Rating;
  focus: Rating;
  mood: Rating;
  stress: Rating;
}

/** One setup being hunted. `patterns` are pattern ids. */
/**
 * A pattern chosen on a hypothesis, in the shape the old client STORES.
 *
 * ⚠ Both halves are kept deliberately. Modelling this as a bare `string[]` of ids
 * is what dropped Josh's 2026-07-30 selection on read, and it is also what would
 * make a write lossy — `name` is stored alongside the id and nothing else in this
 * client could put it back.
 */
export interface HypothesisPattern {
  id: string;
  /** May be empty on a legacy row that stored bare ids. */
  name: string;
}

export interface Hypothesis {
  setup: string;
  patterns: HypothesisPattern[];
  disqualifier: string;
}

export interface PreMarketPlan {
  marketRegime: string;
  gammaRegime: string;
  overallBias: string;
  marketRegimeAnalysis: string;
  gammaRegimeAnalysis: string;
  biasSummary: string;
  newsEvents: NewsEvent[];
  levels: KeyLevels;
  selfAssessment: SelfAssessment;
  hypotheses: Hypothesis[];
  /** Screenshot object paths. Rendering them needs signed URLs — io work. */
  screenshots: string[];
  /** Everything else on the plan, carried verbatim. */
  extra: Readonly<Record<string, unknown>>;
}

/** The readiness gate's per-item ticks. */
export interface ReadinessState {
  items: Readonly<Record<string, unknown>>;
  completedAt: string | null;
}

/** The pre-commitment: what was pledged before the session. */
/**
 * The Session Contract — the trader's pre-committed target and daily loss limit.
 *
 * ── 🚨 ALL NINE KEYS, BECAUSE FOUR MEANT SILENTLY DELETING FIVE ──
 *
 * The old client's `emptySessionContract()` defines nine fields and this modelled
 * four, so any read-then-write would have dropped `lockedAt`, `dllHitAt`,
 * `dllAcknowledgedAt`, `targetHitAt` and `targetAcknowledgedAt`. Confirmed on
 * Josh's 2026-07-30 contract, which carries real values in three of them.
 *
 * That is not a cosmetic loss — the five dropped fields ARE the state machine:
 *
 *   showWarn   personalDLL > 0 && !dllHitAt && pnl <= -0.8 * personalDLL
 *   showHit    dllHitAt && !dllAcknowledgedAt
 *   showTarget targetHitAt && !targetAcknowledgedAt
 *
 * Clearing `dllHitAt` un-hits a limit the trader actually breached. Clearing
 * `dllAcknowledgedAt` re-raises a banner they already dismissed — and it is also
 * the sole input to the old client's post-DLL-override flag, which marks every
 * trade taken after the acknowledgement. Losing it erases the record that they
 * kept trading past their own cap.
 */
export interface SessionContract {
  accountIds: string[];
  targetProfit: number;
  personalDLL: number;
  locked: boolean;
  /** ISO timestamps, or null. Set once and never cleared by the old client. */
  lockedAt: string | null;
  dllHitAt: string | null;
  dllAcknowledgedAt: string | null;
  targetHitAt: string | null;
  targetAcknowledgedAt: string | null;
  /**
   * A self-reported P&L that overrides the computed figure for the check-in.
   *
   * ⚠ ABSENT rather than null when unset — the old client `delete`s the key to
   * clear it and tests `manualPnl != null`, so writing an explicit null is the
   * same as clearing it but a `0` override is real and must survive.
   */
  manualPnl: number | null;
}

export interface EodRecap {
  whatWorked: string;
  whatBroke: string;
  /** ⚠ Semantically "things I need to fix" — see the header. */
  mistakes: string;
  /** ⚠ Semantically "things I did well" — see the header. */
  wins: string;
  lessons: string;
  tomorrowFocus: string;
  mood: string;
  energy: string;
}

export interface MidSessionNote {
  id: string;
  time: string;
  text: string;
}

export interface JournalEntry {
  date: string;
  preMarketPlan: PreMarketPlan;
  midSessionNotes: MidSessionNote[];
  eodRecap: EodRecap;
  booleans: Record<string, unknown>;
  disqualifiers: string;
  /** Set by the old client's weekend re-key; present only on moved entries. */
  originalCalendarDate: string | null;
  createdAt: string | null;
  readiness: ReadinessState;
  sessionContract: SessionContract;
  /** Voice-note object paths. Same signed-URL caveat as screenshots. */
  recordings: string[];
}

/** The marker the old client writes once an entry's recap has been un-swapped. */
export const EOD_SWAP_MARKER = '_eodRecapSwapMigrated';

/**
 * When the old client fixed its crossed HTML input ids — commit `4f6d9d8`,
 * "Phase 2 audit 1.6: fix eodRecap.mistakes <-> wins inversion (REAL BUG)",
 * 2026-06-03 12:27:16 -0400.
 *
 * Entries WRITTEN BEFORE this hold inverted values on disk. Entries written
 * after it are already correct, whatever their marker says.
 */
export const EOD_SWAP_FIX_SHIPPED = Date.parse('2026-06-03T16:27:16Z');

/**
 * Un-invert `mistakes` / `wins` for an entry that has not been corrected yet.
 *
 * ── 🚨 THE MARKER ALONE IS NOT SUFFICIENT, AND TRUSTING IT CORRUPTED REAL DATA ──
 *
 * The first version gated only on the marker: absent ⇒ swap. That is wrong, and
 * it was caught on live data (2026-07-30) rather than by any test.
 *
 * The old client stamps the marker inside `migrateState`, which runs at PAGE LOAD
 * over the journal it just fetched. An entry the user CREATES during that session
 * is never seen by it — so the row is written with correct orientation and an
 * EMPTY `booleans`. "No marker" therefore means one of two opposite things:
 *
 *   written BEFORE 2026-06-03, never migrated  → inverted on disk  → swap
 *   CREATED IN-SESSION after 2026-06-03        → correct on disk   → DO NOT SWAP
 *
 * Josh's 2026-07-30 entry was the second kind: `eod_mistakes` held the text he
 * typed into "Things I Need to Fix", `booleans` was `{}`, and this function
 * filed it under "Things I did well". Confirmed against his beta screenshot.
 *
 * So the discriminator is `createdAt`, not the marker.
 *
 * ⚠ AND WHEN `createdAt` IS UNKNOWN, DO NOT SWAP. The two errors are not
 * symmetric: displaying a legacy entry crossed is visible and reversible, while
 * swapping a correct one and letting a save persist it is neither — after that
 * the marker is set, no client ever swaps again, and the corruption is
 * indistinguishable from real data.
 */
export function applyEodSwap(j: JournalEntry): JournalEntry {
  if (j.booleans[EOD_SWAP_MARKER] === true) return j;

  const created = j.createdAt ? Date.parse(j.createdAt) : NaN;
  const writtenBeforeFix = Number.isFinite(created) && created < EOD_SWAP_FIX_SHIPPED;

  if (!writtenBeforeFix) {
    // Correct on disk already. Stamp the marker so the claim travels with the
    // row on the next save — which also stops the OLD client swapping it, since
    // its migration checks the same marker.
    return { ...j, booleans: { ...j.booleans, [EOD_SWAP_MARKER]: true } };
  }

  return {
    ...j,
    eodRecap: {
      ...j.eodRecap,
      // ⚠ `|| ''` matching the old client: a null on either side becomes an
      // empty string rather than travelling into the other field as null.
      mistakes: j.eodRecap.wins || '',
      wins: j.eodRecap.mistakes || '',
    },
    // Marked in memory so a second pass is a no-op — the old client relies on
    // the same idempotence, and this module is applied on every read.
    booleans: { ...j.booleans, [EOD_SWAP_MARKER]: true },
  };
}

/**
 * The 14 flat numeric level keys, as the old client stores them.
 *
 * ⚠ FLAT on the plan object, not nested under `levels` — the nesting exists only
 * in this client's typed view. Hoisted to module scope so the writer and the
 * reader cannot drift apart: a key in one list and not the other is a field that
 * reads fine and vanishes on save.
 */
export const LEVEL_KEYS = [
  'pdVah', 'pdVal', 'pdPoc', 'prevHigh', 'prevLow',
  'overnightHigh', 'overnightLow', 'callWall1', 'callWall2',
  'putWall1', 'putWall2', 'hvl', 'mvc', 'lis',
] as const;

/**
 * The seven keys the old client STRIPS from the blob before writing.
 *
 * They live in their own flattened columns, and `_extractExtras(p, planKnown)` in
 * the old client keeps them out of `pre_market_plan` entirely. That matters more
 * than it looks: the old client's LOADER spreads the blob LAST, so a copy left in
 * the blob would OVERRIDE the column and the two clients would disagree — this
 * client reads the column first. Stripping is what makes the question moot.
 */
export const PLAN_COLUMN_KEYS = [
  'marketRegime', 'gammaRegime', 'overallBias',
  'marketRegimeAnalysis', 'gammaRegimeAnalysis', 'biasSummary', 'disqualifiers',
] as const;

/**
 * Serialise a plan back to the `pre_market_plan` blob.
 *
 * ── 🚨 A MERGE OVER THE RAW STORED OBJECT, NEVER A REBUILD ──
 *
 * The typed model is the best it has ever been — three round-trip losses were
 * closed today — but "the model is complete" is a claim that was wrong three times
 * already, and each time it was wrong silently. So the writer does not trust it:
 * it starts from the row's OWN blob and overwrites only the keys it understands.
 * A field this client has never heard of survives because it was never touched.
 *
 * ⚠ KNOWN LIMIT — LIST ITEMS ARE REBUILT, NOT MERGED. Each `hypos[i]`,
 * `newsEvents[i]` and `patterns[i]` is reconstructed from the modelled fields, so
 * an unknown key INSIDE one of those objects would be dropped. Merging them by
 * index was rejected: the user can reorder, insert and delete these, and an index
 * merge after a reorder attributes one item's data to another — corrupting rather
 * than losing, which is worse.
 *
 * Today no such key exists; the `HYPO_KEYS` / `NEWS_KEYS` / `PATTERN_KEYS` tripwire
 * in `planRoundTrip.test.ts` fails the build if the old client ever adds one, so
 * the decision gets made deliberately instead of discovered afterwards.
 *
 * @param raw the blob exactly as it was read for THIS row. Passing `{}` here
 *            turns the merge back into a rebuild and re-opens every hazard above.
 */
/**
 * Drop News & Events rows the trader typed nothing into, keeping at least one.
 *
 * ── 🚨 WHY THIS IS A RULE AND NOT A `.filter()` AT THE CALL SITE ──
 *
 * "Blank" has to mean the same thing everywhere or the form and the record
 * disagree about what was saved. It is the old client's own test, verbatim
 * (index.html:34609-34612): a row survives if ANY of its three fields has
 * non-whitespace in it.
 *
 * ⚠ ALL THREE FIELDS, AND `time` IS THE ONE THAT MATTERS. This module's other
 * emptiness test, `isJournalEntryEmpty`, reads only `event` and `plan` — it is
 * asking "has this trader journalled at all", where a bare time is not an
 * answer. Reusing that predicate here would silently delete the row of someone
 * who typed 8:30, went to look up the release name, and came back.
 *
 * ⚠ NEVER RETURNS AN EMPTY LIST. `normalisePlan` seeds a row for the same
 * reason: a plan with no rows renders as a section with no fields, so stripping
 * the last blank row would take away the place to type. Beta re-seeds too
 * (index.html:34613).
 *
 * ⚠ RETURNS THE SAME ARRAY when nothing was stripped, so a caller can use it as
 * a cheap "did anything change" test without a deep compare.
 *
 * ── 🔴 THE RE-SEED IS WRITTEN SO IT CANNOT LAUNDER A WRONG PREDICATE ──
 *
 * A first version short-circuited: "if nothing survived and there was exactly
 * one row, hand that row back" — an identity optimisation, since the only row
 * must itself be blank. It also meant a predicate that wrongly judged a
 * half-filled row blank RETURNED THAT ROW ANYWAY, so the negative control
 * written to catch exactly that mistake passed while the mistake was present.
 * MEASURED: the single-row control stayed green with `time` dropped from the
 * test below. A fresh blank row costs nothing and cannot hide anything.
 */
export function stripEmptyNewsRows(rows: readonly NewsEvent[]): readonly NewsEvent[] {
  const kept = rows.filter(
    (n) => n.time.trim() !== '' || n.event.trim() !== '' || n.plan.trim() !== '',
  );
  if (kept.length === rows.length) return rows;
  return kept.length ? kept : [{ time: '', event: '', plan: '' }];
}

export function planToBlob(
  plan: PreMarketPlan,
  raw: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...raw };

  // ── 🚨 THE 14 LEVEL KEYS AND `lisSummary` ARE CARRIED, NOT WRITTEN ──
  //
  // The old client REMOVED those inputs in v2. Its own comment
  // (index.html:33262) is explicit: they "ride along untouched" through the plan
  // spread. The values still exist on historical rows and still feed the AI coach
  // prompt via `formatPreMarketStructured` — they simply cannot be entered.
  //
  // So this writer must not touch them either. The `{ ...raw }` above already
  // carries them; writing them back from `plan.levels` would be a no-op TODAY and
  // a silent wipe the moment anything upstream fails to parse one. Nothing here
  // may edit a field the user has no control over.
  //
  // ⚠ `normalisePlan` still PARSES them — reading is fine and will be needed when
  // the coach prompt is ported. Only the write is removed.

  // ⚠ Written under `hypos`, the key the old client actually stores. `hypotheses`
  // is accepted on READ for safety, but writing both would leave two copies to
  // disagree — so any legacy `hypotheses` key is removed.
  out['hypos'] = plan.hypotheses.map((h) => ({
    setup: h.setup,
    patterns: h.patterns.map((p) => ({ id: p.id, name: p.name })),
    disqualifier: h.disqualifier,
  }));
  delete out['hypotheses'];

  out['newsEvents'] = plan.newsEvents.map((n) => ({
    time: n.time, event: n.event, plan: n.plan,
  }));
  out['screenshots'] = [...plan.screenshots];

  // ── 🚨 MERGED OVER THE STORED SUB-OBJECT, NOT REBUILT ──
  //
  // Rebuilding this from the six modelled fields drops any OTHER key inside
  // `selfAssessment` — and `selfAssessment` is in the reader's `known` set, so
  // those keys are not rescued by `extra` either. That is a loss the top-level
  // merge cannot catch, and the first version of this function had it.
  //
  // ⚠ An UNSET rating is DELETED, not written as null. The old client's × clears a
  // slider by removing the key, and its "any rating recorded?" test reads an
  // explicit null as a recorded value.
  const rawSa = (raw['selfAssessment'] && typeof raw['selfAssessment'] === 'object')
    ? { ...(raw['selfAssessment'] as Record<string, unknown>) } : {};
  const setOrDelete = (k: string, v: unknown) => {
    if (v == null) delete rawSa[k]; else rawSa[k] = v;
  };
  setOrDelete('sleepHours', plan.selfAssessment.sleepHours);
  setOrDelete('caffeineCups', plan.selfAssessment.caffeineCups);
  for (const k of ['energy', 'focus', 'mood', 'stress'] as const) {
    setOrDelete(k, plan.selfAssessment[k]);
  }
  out['selfAssessment'] = rawSa;

  // ⚠ Unknown keys are restored from `extra` rather than left to the spread alone,
  // so a caller that edited `extra` is honoured too.
  for (const [k, v] of Object.entries(plan.extra)) out[k] = v;

  // The seven that belong to columns never travel in the blob. See PLAN_COLUMN_KEYS.
  for (const k of PLAN_COLUMN_KEYS) delete out[k];

  return out;
}

/**
 * Normalise a legacy pre-market plan.
 *
 * ⚠ `newsEvents` always ends up with at least one row, empty if need be. The old
 * client's renderer indexes into `newsEvents[0]`, so an empty array is a blank
 * screen rather than a blank field.
 */
export function normalisePlan(raw: Record<string, unknown>): PreMarketPlan {
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');

  let newsEvents: NewsEvent[];
  if (Array.isArray(raw['newsEvents'])) {
    newsEvents = (raw['newsEvents'] as unknown[]).map((e) => {
      const o = (e && typeof e === 'object') ? e as Record<string, unknown> : {};
      // `time` is backfilled because it postdates the time/event split.
      return { time: str(o['time']), event: str(o['event']), plan: str(o['plan']) };
    });
  } else {
    const legacy = str(raw['news']).trim();
    newsEvents = legacy
      ? [{ time: '', event: legacy, plan: '' }]
      : [{ time: '', event: '', plan: '' }];
  }
  if (!newsEvents.length) newsEvents = [{ time: '', event: '', plan: '' }];

  const numOrNull = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const levels = { lisSummary: str(raw['lisSummary']) } as KeyLevels;
  for (const k of LEVEL_KEYS) levels[k] = numOrNull(raw[k]);

  const sa = (raw['selfAssessment'] ?? {}) as Record<string, unknown>;
  /**
   * ⚠ A rating survives AS STORED. `str()` here turned every slider value into
   * `''` because they are numbers — the whole section then read "Not recorded".
   * Numbers stay numbers, legacy text labels stay text, and anything else becomes
   * null rather than a misleading empty string.
   */
  const ratingOrNull = (v: unknown): Rating => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim()) return v;
    return null;
  };
  const selfAssessment: SelfAssessment = {
    sleepHours: numOrNull(sa['sleepHours']),
    caffeineCups: numOrNull(sa['caffeineCups']),
    energy: ratingOrNull(sa['energy']),
    focus: ratingOrNull(sa['focus']),
    mood: ratingOrNull(sa['mood']),
    stress: ratingOrNull(sa['stress']),
  };

  // ⚠ `hypos` is the stored key; `hypotheses` is accepted too because the old
  // client's own comments use both names and a rename would silently empty the
  // section rather than fail.
  const rawHypos = Array.isArray(raw['hypos']) ? raw['hypos']
    : Array.isArray(raw['hypotheses']) ? raw['hypotheses'] : [];
  const hypotheses: Hypothesis[] = (rawHypos as unknown[]).map((h) => {
    const o = (h && typeof h === 'object') ? h as Record<string, unknown> : {};
    return {
      setup: str(o['setup']),
      // ── 🚨 THE STORED SHAPE IS `{id, name}`, AND FILTERING TO STRINGS ATE IT ──
      //
      // The first version kept only `typeof x === 'string'`, which drops every
      // real selection: the old client stores
      // `patterns: [{ id: 'm80h5npdg9nz', name: 'VA REVERSION' }]`. So a
      // hypothesis whose only content was its pattern came back with
      // `patterns: []` and vanished from the screen entirely — found on Josh's
      // 2026-07-30 entry, where the section read "Setups hunting (0)".
      //
      // Keeping BOTH fields is also what makes a write safe: the name is the
      // half a `string[]` model could never put back, so a round trip through
      // the old shape would have silently stripped it.
      //
      // A bare string id is still accepted — legacy rows may carry that.
      patterns: Array.isArray(o['patterns'])
        ? (o['patterns'] as unknown[]).flatMap((x): HypothesisPattern[] => {
          if (typeof x === 'string') return x ? [{ id: x, name: '' }] : [];
          if (x && typeof x === 'object') {
            const px = x as Record<string, unknown>;
            const id = str(px['id']);
            return id ? [{ id, name: str(px['name']) }] : [];
          }
          return [];
        })
        : [],
      disqualifier: str(o['disqualifier']),
    };
  });

  const screenshots = Array.isArray(raw['screenshots'])
    ? (raw['screenshots'] as unknown[]).filter((x): x is string => typeof x === 'string')
    : [];

  const known = new Set([
    'newsEvents', 'news', 'bias', 'keyLevels', 'marketRegime', 'gammaRegime',
    'overallBias', 'marketRegimeAnalysis', 'gammaRegimeAnalysis', 'biasSummary',
    'selfAssessment', 'hypos', 'hypotheses', 'screenshots', 'lisSummary',
    ...LEVEL_KEYS,
  ]);
  const extra: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) if (!known.has(k)) extra[k] = v;

  return {
    marketRegime: str(raw['marketRegime']),
    gammaRegime: str(raw['gammaRegime']),
    overallBias: str(raw['overallBias']),
    // Legacy `bias` → marketRegimeAnalysis; legacy `keyLevels` → gammaRegimeAnalysis.
    marketRegimeAnalysis: typeof raw['marketRegimeAnalysis'] === 'string'
      ? raw['marketRegimeAnalysis'] as string
      : str(raw['bias']),
    gammaRegimeAnalysis: typeof raw['gammaRegimeAnalysis'] === 'string'
      ? raw['gammaRegimeAnalysis'] as string
      : str(raw['keyLevels']),
    biasSummary: str(raw['biasSummary']),
    newsEvents,
    levels,
    selfAssessment,
    hypotheses,
    screenshots,
    extra,
  };
}

/**
 * An empty entry for a date that has no row.
 *
 * ⚠ IN MEMORY ONLY — creating this does not create a row, and must not. The old
 * client's whole-state sync DELETES any date present in its snapshot and absent
 * from live state, so a client that materialises rows for every date the user
 * merely looked at is manufacturing rows for the other client to churn.
 * `saveJournalRecap`'s upsert is what creates the row, on an explicit save.
 *
 * ⚠ The swap marker is set here for the same reason `applyEodSwap` sets it: a new
 * entry has empty recap fields, so the swap is a no-op and the marker records
 * "this row is in corrected orientation" — which is true, and which stops the old
 * client swapping the first prose ever written into it.
 */
export function blankEntry(date: string): JournalEntry {
  return {
    date,
    preMarketPlan: normalisePlan({}),
    midSessionNotes: [],
    eodRecap: {
      whatWorked: '', whatBroke: '', mistakes: '', wins: '',
      lessons: '', tomorrowFocus: '', mood: '', energy: '',
    },
    booleans: { [EOD_SWAP_MARKER]: true },
    disqualifiers: '',
    originalCalendarDate: null,
    createdAt: null,
    readiness: { items: {}, completedAt: null },
    // Transcribed from the old client's `emptySessionContract()`.
    sessionContract: {
      accountIds: [], targetProfit: 0, personalDLL: 0,
      locked: false, lockedAt: null,
      dllHitAt: null, dllAcknowledgedAt: null,
      targetHitAt: null, targetAcknowledgedAt: null,
      manualPnl: null,
    },
    recordings: [],
  };
}

/** Nothing written on any surface. Mirrors the old client's emptiness test. */
export function isJournalEntryEmpty(j: JournalEntry): boolean {
  const p = j.preMarketPlan;
  const anyLevel = Object.entries(p.levels)
    .some(([k, v]) => (k === 'lisSummary' ? String(v).trim() !== '' : v != null));
  const anySelf = Object.values(p.selfAssessment).some((v) => v != null && v !== '');
  const anyPlan =
    p.marketRegime || p.gammaRegime || p.overallBias ||
    p.marketRegimeAnalysis || p.gammaRegimeAnalysis || p.biasSummary ||
    p.newsEvents.some((n) => n.event.trim() || n.plan.trim()) ||
    p.hypotheses.some((h) => h.setup.trim() || h.disqualifier.trim() || h.patterns.length) ||
    p.screenshots.length || anyLevel || anySelf;
  const r = j.eodRecap;
  const anyRecap =
    r.whatWorked || r.whatBroke || r.mistakes || r.wins ||
    r.lessons || r.tomorrowFocus || r.mood || r.energy;
  // ⚠ The swap marker does NOT count as content. It is written by a migration,
  // so counting it would make every entry non-empty the moment it was read.
  const userBooleans = Object.keys(j.booleans).filter((k) => !k.startsWith('_'));
  return !anyPlan && !anyRecap && !j.midSessionNotes.length &&
    !j.disqualifiers.trim() && !userBooleans.length;
}

// =============================================================================
// TILT CHECK — four behavioural flags over one day's trades.
// -----------------------------------------------------------------------------
// PORTED FROM `dvTiltCheck`. Read-only; the Session phase shows it.
//
// ⚠ THE UNIT SPLIT IS DELIBERATE and mirrors computeKpis: the streak and
// worst-loss logic run on the $ SIGN, which is unit-invariant, while the
// "P&L after your worst loss" MAGNITUDE is summed in the display unit. Deriving
// the sign per unit would change which trades count as losses under `points`.
// =============================================================================

/** What the tilt check needs from a trade. */
export interface TiltTrade {
  timeIn: string | null;
  timeOut: string | null;
  netPnl: number | null;
  qty: number | null;
  /** The display-unit magnitude. Equals `netPnl` in `usd`. */
  unitValue: number | null;
}

export interface TiltResult {
  /** Longest run of consecutive losers. 3+ is a flag. */
  maxStreak: number;
  /** How many trades came after the worst loss. */
  afterN: number;
  /** Their summed P&L, in the display unit. Negative is a flag. */
  afterPnl: number;
  /** Re-entries within 3 minutes of closing a loser. */
  revenge: number;
  /** Trades sized UP immediately after a loser. */
  sizeUp: number;
  /** How many of the four fired. */
  flags: number;
}

/** `"09:31:00"` → minutes since midnight. Null when unparseable. */
export function clockToMinutes(t: string | null | undefined): number | null {
  const m = String(t ?? '').match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return h * 60 + min;
}

/**
 * ⚠ Returns null under two trades rather than a zeroed result. "No tilt
 * detected" and "not enough trades to look" are different claims, and the first
 * is reassurance the data cannot support.
 */
export function tiltCheck(dayTrades: readonly TiltTrade[]): TiltResult | null {
  const ts = dayTrades
    .map((t) => ({
      tin: clockToMinutes(t.timeIn),
      // A trade with no exit time closes when it opened, so a still-open
      // position cannot manufacture a gap.
      tout: clockToMinutes(t.timeOut) ?? clockToMinutes(t.timeIn),
      pnl: t.netPnl ?? 0,
      pnlDisp: t.unitValue ?? 0,
      qty: Number(t.qty) || 0,
    }))
    .filter((t) => t.tin != null)
    .sort((a, b) => (a.tin as number) - (b.tin as number));

  if (ts.length < 2) return null;

  let streak = 0;
  let maxStreak = 0;
  for (const t of ts) {
    if (t.pnl < 0) { streak++; if (streak > maxStreak) maxStreak = streak; }
    else streak = 0;
  }

  // ⚠ Strict `<` against a 0 seed, ported literally: a day with no losing trade
  // has no "worst loss", so nothing counts as coming after one.
  let worstIdx = -1;
  let worstPnl = 0;
  ts.forEach((t, i) => { if (t.pnl < worstPnl) { worstPnl = t.pnl; worstIdx = i; } });

  const after = worstIdx >= 0 ? ts.slice(worstIdx + 1) : [];
  const afterPnl = after.reduce((s, t) => s + t.pnlDisp, 0);

  let revenge = 0;
  let sizeUp = 0;
  for (let i = 1; i < ts.length; i++) {
    const prev = ts[i - 1]!;
    const cur = ts[i]!;
    if (prev.pnl < 0 && prev.tout != null) {
      const gap = (cur.tin as number) - prev.tout;
      // ⚠ `gap >= 0` excludes overlapping trades: a position opened BEFORE the
      // loser closed was not a reaction to it.
      if (gap >= 0 && gap <= 3) revenge++;
    }
    if (prev.pnl < 0 && cur.qty > 0 && prev.qty > 0 && cur.qty > prev.qty) sizeUp++;
  }

  const flags =
    (maxStreak >= 3 ? 1 : 0) +
    (after.length && afterPnl < 0 ? 1 : 0) +
    (revenge > 0 ? 1 : 0) +
    (sizeUp > 0 ? 1 : 0);

  return { maxStreak, afterN: after.length, afterPnl, revenge, sizeUp, flags };
}

// ---------------------------------------------------------------------------
// The session contract — the pre-commitment, and the state machine beside it
// ---------------------------------------------------------------------------
//
// ── 🚨 THE FORM OWNS FIVE OF THE NINE FIELDS. THE OTHER FOUR ARE EVIDENCE ──
//
// `accountIds` · `targetProfit` · `personalDLL` · `locked` · `lockedAt` are the
// pledge, and a trader edits them before the session.
//
// `dllHitAt` · `dllAcknowledgedAt` · `targetHitAt` · `targetAcknowledgedAt` (and
// `manualPnl`) are written DURING the session by the P&L watcher, and they are the
// record of whether the pledge held. `dllAcknowledgedAt` in particular is
// string-compared against every later trade's timestamp to flag it as an override
// of the trader's own daily stop — so overwriting one does not lose a preference,
// it rewrites the history of a breach.
//
// That is why `contractToBlob` exists in the same shape as `planToBlob`: merge
// over the stored object, touch only the five, and let everything else — modelled
// or not — survive by never being written.

/** The five keys the contract FORM is allowed to write. Asserted by a test. */
export const CONTRACT_FORM_KEYS = [
  'accountIds', 'targetProfit', 'personalDLL', 'locked', 'lockedAt',
] as const;

/**
 * Can this contract be locked?
 *
 * ⚠ The old client's rule verbatim: **a target OR a personal DLL (or both)**, and
 * it toasts rather than locking when neither is set. A contract pledging nothing
 * is not a pre-commitment — it is a timestamp — and the Session view's 80% / 100%
 * warnings have nothing to fire against.
 *
 * Accounts stay optional on purpose: empty means ALL accounts, matching Goals.
 * Requiring one here would be a new rule, and a stricter one than beta's.
 */
export function canLockContract(c: Pick<SessionContract, 'targetProfit' | 'personalDLL'>): boolean {
  return c.targetProfit > 0 || c.personalDLL > 0;
}

/**
 * Serialise the contract back to the `session_contract` blob.
 *
 * ── 🚨 A MERGE OVER THE RAW STORED OBJECT, NEVER A REBUILD ──
 *
 * Same argument as `planToBlob`, and a sharper one: the four timestamps this
 * function refuses to write are the only record that a daily stop was breached and
 * when the trader acknowledged it. Rebuilding the blob from the typed model would
 * write them back from whatever the form happened to be holding — and a form that
 * has been open since before the breach is holding `null`.
 *
 * ⚠ `manualPnl` is not written either. The old client `delete`s the key to clear
 * it and tests `!= null`, so an explicit `null` from here would read as "cleared"
 * — indistinguishable from a real clear, and it would silently discard a
 * self-reported figure entered on the Session tab.
 *
 * @param raw the blob exactly as it was read for THIS row. `{}` only for a date
 *            with no row yet; anything else re-opens every hazard above.
 */
export function contractToBlob(
  c: SessionContract,
  raw: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  return {
    ...raw,
    accountIds: [...c.accountIds],
    targetProfit: c.targetProfit,
    personalDLL: c.personalDLL,
    locked: c.locked,
    lockedAt: c.lockedAt,
  };
}

/**
 * The contract blob with ONLY `manualPnl` changed — the Session view's check-in.
 *
 * ── 🚨 A SEPARATE FUNCTION BECAUSE IT OWNS A DIFFERENT KEY ──
 *
 * `contractToBlob` writes the five fields the pre-market FORM owns and refuses
 * to write `manualPnl`. This writes `manualPnl` and refuses to write the five.
 * Two surfaces, two owners, one blob — and folding them together would give the
 * planning form the power to overwrite a figure typed mid-session, which is the
 * direction the whole D62 rule points away from.
 *
 * ⚠ THE KEY IS DELETED, NOT NULLED, WHEN CLEARED. The old client `delete`s it
 * and every reader tests `manualPnl != null`. Writing an explicit `null` would
 * behave the same today and read as "a value of null" to anything that checks
 * `'manualPnl' in c` tomorrow — and `0` is a REAL override that must survive, so
 * falsiness cannot be the test.
 */
export function checkInToBlob(
  manualPnl: number | null,
  raw: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const next = { ...raw };
  if (manualPnl == null || !Number.isFinite(manualPnl)) delete next['manualPnl'];
  else next['manualPnl'] = manualPnl;
  return next;
}

// ---------------------------------------------------------------------------
// The Session view's contract check-in
// ---------------------------------------------------------------------------

/** What the check-in banner says, and which way it leans. */
export interface CheckInVerdict {
  /** `pos` tints blue, `neg` red — the old client's two banner variants. */
  tone: 'pos' | 'neg';
  kind: 'stopped' | 'warning' | 'target' | 'onContract';
  /** The P&L the verdict was reached on — the override when one is set. */
  pnl: number;
  /** How much of the daily loss limit is used, 0–100. Zero on a green day. */
  dllPct: number;
  /** Dollars left before the pledged stop. Zero when no limit is set. */
  room: number;
  /** True when the figure is self-reported rather than from imported trades. */
  override: boolean;
}

/**
 * Is there a contract to check in against?
 *
 * ⚠ A TARGET **OR** A LIMIT. Accounts are deliberately not part of the test —
 * empty means all accounts, matching Goals — and requiring one would be a
 * stricter rule than the old client's.
 */
export function hasCheckInContract(
  c: Pick<SessionContract, 'targetProfit' | 'personalDLL'>,
): boolean {
  return !!(c.targetProfit || c.personalDLL);
}

/**
 * Where the trader stands against their own pre-commitment.
 *
 * ── ⚠ THE ORDER OF THE FOUR CASES IS THE WHOLE BEHAVIOUR ──
 *
 * Beta tests the daily stop FIRST, then the 80% warning, and only then the
 * profit target. A day that hits both the target and the loss limit is a day
 * that must be told to stop, and reversing the first two clauses would
 * congratulate a trader who has just breached their own limit.
 *
 * ⚠ THE LIMIT ONLY COUNTS AGAINST A LOSS. `dllPct` is 0 whenever P&L is
 * positive: a green day uses none of the allowance, and the percentage is
 * capped at 100 so a catastrophic day reads "100%", not "340%".
 *
 * @param pnl the figure to judge — the caller resolves the override first.
 */
export function checkInVerdict(
  c: Pick<SessionContract, 'targetProfit' | 'personalDLL'>,
  pnl: number,
  override: boolean,
): CheckInVerdict {
  const dll = Number(c.personalDLL) || 0;
  const target = Number(c.targetProfit) || 0;
  const dllPct = dll && pnl < 0 ? Math.round(Math.min(1, -pnl / dll) * 100) : 0;
  const room = dll ? Math.max(0, dll + Math.min(0, pnl)) : 0;
  const base = { pnl, dllPct, room, override };

  if (dll && pnl <= -dll) return { ...base, tone: 'neg', kind: 'stopped' };
  if (dll && dllPct >= 80) return { ...base, tone: 'neg', kind: 'warning' };
  if (target && pnl >= target) return { ...base, tone: 'pos', kind: 'target' };
  return { ...base, tone: 'pos', kind: 'onContract' };
}

// ---------------------------------------------------------------------------
// Mid-session notes
// ---------------------------------------------------------------------------

/**
 * The note list, serialised for `mid_session_notes`.
 *
 * ── ⚠ TWO KEYS, AND `id` IS DELIBERATELY NOT ONE OF THEM ──
 *
 * The old client's `addNote` pushes `{ time, text }` and nothing else, and it is
 * the only thing that has ever written this column. `journalEntryFromDb` invents
 * an `id` when the stored object has none, purely so React has a stable key —
 * writing that invented id back would put a field in the database that no
 * version of either client has ever stored.
 *
 * That is D62 read the other way round: the rule is not "send every field you
 * hold", it is "send exactly what you own". This form owns the time and the
 * text.
 *
 * ⚠ THE LIST IS SENT WHOLE, WHICH IS ONLY SAFE BECAUSE IT IS READ WHOLE — the
 * same condition `saveRecordings` states. `loadJournal` reads every element and
 * the Session view renders every element, so the array in memory IS the array on
 * disk. If that read ever becomes partial, this write becomes a truncation.
 */
export function notesToRows(
  notes: readonly MidSessionNote[],
): { time: string; text: string }[] {
  return notes.map((n) => ({ time: n.time, text: n.text }));
}

/**
 * A new note, stamped from an injected clock.
 *
 * ⚠ NO WALL CLOCK IN HERE (D26) — the caller passes the time. The old client
 * calls `new Date()` inline; this layer is pure and the Session view supplies it.
 *
 * ⚠ `HH:MM` LOCAL, not an ISO instant. That is what beta stores and what the
 * note rows display, and a note is a marker inside the trader's own session —
 * "10:42, chased that one" — so the local wall time IS the meaning.
 */
export function stampNote(text: string, now: Date): MidSessionNote {
  const p = (n: number) => String(n).padStart(2, '0');
  return {
    id: `n${now.getTime()}`,
    time: `${p(now.getHours())}:${p(now.getMinutes())}`,
    text: text.trim(),
  };
}

// ---------------------------------------------------------------------------
// The morning's hypotheses, offered to the evening's recap (TCR-88)
// ---------------------------------------------------------------------------

/**
 * One hypothesis as a line of RECAP PROSE.
 *
 * ── 🔴 WHY TEXT AND NOT A STRUCTURED LIST ──
 *
 * `whatWorked` and `whatBroke` are plain strings, and that string is read as
 * prose by five places in beta alone — three Markdown exports, the HTML day
 * render and the share payload — each of them literally
 * `md += \`Worked: ${j.eodRecap.whatWorked}\``. This client feeds the same
 * string into the coach brief. A parallel array of picked ids would be invisible
 * to every one of them: the recap would look full on screen and read as empty in
 * every export, and beta would be writing a key it never populates.
 *
 * ── 🔴 WHAT THE LINE CONTAINS, AND WHY (criterion 7) ──
 *
 * **The setup and its pattern names, together.** Not the setup alone.
 *
 * `HypothesisPattern` stores `{id, name}` rather than a bare id precisely
 * because nothing else in this client can put the name back — modelling it as
 * ids is what dropped Josh's 2026-07-30 selection on read. The recap line is the
 * one place that pairing earns its keep in prose: "Fade the open — VA Reversion"
 * tells a reader in an export WHICH setup was tested, where a bare "Fade the
 * open" loses it and a bare pattern name loses the idea.
 *
 * ⚠ EITHER HALF MAY BE EMPTY and the other still carries the line. A hypothesis
 * whose only content was its pattern is a real stored shape — see the read's own
 * note on `patterns` — and a hypothesis with a setup and no pattern is the normal
 * case early in the morning.
 */
export function hypothesisLine(h: Hypothesis): string {
  // Local, because the module-scope helper lives inside the read (:463).
  const txt = (v: unknown): string => (typeof v === 'string' ? v : '');
  const setup = txt(h.setup).trim();
  const names = (h.patterns ?? [])
    .map((p) => txt(p.name).trim())
    .filter(Boolean)
    .join(', ');
  if (setup && names) return `${setup} — ${names}`;
  return setup || names;
}

/**
 * The hypotheses there is anything to offer FROM, each with its line.
 *
 * ⚠ A HYPOTHESIS WITH NO SETUP AND NO NAMED PATTERN IS NOTHING TO OFFER, not an
 * empty chip. The plan editor keeps blank rows while the trader is still
 * thinking, so a picker built off the raw array would show buttons with no
 * label — and `domain/journal.ts`'s read records that a hypothesis carrying only
 * a pattern has genuinely come back from the database.
 */
export function offerableHypotheses(
  plan: PreMarketPlan | null | undefined,
): readonly { index: number; line: string }[] {
  const out: { index: number; line: string }[] = [];
  (plan?.hypotheses ?? []).forEach((h, index) => {
    const line = hypothesisLine(h);
    if (line) out.push({ index, line });
  });
  return out;
}

/**
 * Add a hypothesis line to what the trader has already written.
 *
 * ⚠ APPENDS ON ITS OWN LINE, and never in front of existing prose — the trader's
 * own words are the record and a pick is an addition to it.
 *
 * ⚠ IDEMPOTENT, deliberately. Two picks must not overwrite each other, and a
 * double-click must not read as two separate observations about the same
 * hypothesis. Compared line by line and trimmed, so the same hypothesis picked
 * twice is a no-op while a line the trader has since EDITED is a different line
 * and gets added.
 */
export function appendHypothesis(existing: string, line: string): string {
  const add = line.trim();
  if (!add) return existing;
  const already = existing.split('\n').some((l) => l.trim() === add);
  if (already) return existing;
  const base = existing.replace(/\s+$/, '');
  return base ? `${base}\n${add}` : add;
}
